import { Link } from 'react-router-dom';
import StartChooser from '../../components/StartChooser';
import { useTranslation } from 'react-i18next';
import SEO, { breadcrumbJsonLd, faqJsonLd } from '../../components/SEO';
import { features } from './data';
import { industries } from '../industries/data';
import { getPostBySlug } from '../blog/data';

const DEMO_URL = 'https://meetings.hubspot.com/aditya145';

export type PlanName = 'Lite' | 'Professional' | 'Premium' | 'Enterprise';

export interface FeatureImage {
  src: string;
  alt: string;
  width: number;
  height: number;
}

/** An image-led deep dive into one part of the feature. Rendered alternating left/right. */
export interface FeatureSpotlight {
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  image: FeatureImage;
  /** Phone screens and other tall visuals sit centred on a tinted panel instead of filling the frame. */
  fit?: 'contain';
}

export interface FeatureData {
  slug: string;
  icon: string;
  name: string;
  tagline: string;
  headline: string;
  headlineHighlight: string;
  description: string;
  /** Accent color for this feature (like Built for / industry pages). Defaults to #1769ff. */
  accentColor?: string;
  /** Light background tint for badges/cards. Defaults to #eef5ff. */
  accentColorLight?: string;
  /** Lowest plan that includes the feature. Mirrors src/i18n/local/en/pricing.ts. */
  plan?: PlanName;
  /** Lowest Geofleet Lite plan with the feature, when Lite has it but Command only from `plan`. */
  liteFrom?: 'Lite' | 'Lite Plus' | 'Lite Autopilot';
  heroStats: { value: string; label: string }[];
  /** Main product visual shown beside the hero copy. Portrait and square images get a narrower frame. */
  heroImage: FeatureImage;
  /** Optional gallery of app screens shown after the hero (e.g. Driver App) */
  heroScreens?: { src: string; title: string }[];
  /** The day-to-day pain the feature removes, and what replaces it. Keep each list to 3–4 short lines. */
  beforeAfter?: { before: string[]; after: string[] };
  spotlights?: FeatureSpotlight[];
  howItWorks: { step: string; title: string; description: string; icon: string }[];
  capabilities: { icon: string; title: string; description: string; tag?: string }[];
  /** Industry pages where this feature matters most, with a one-line reason each. */
  useCases?: { industry: string; title: string; description: string }[];
  benefits: { value: string; label: string; description: string }[];
  integrations?: string[];
  faq: { question: string; answer: string }[];
  /** Blog slugs shown under "Keep reading". Unknown slugs are skipped. */
  relatedPosts?: string[];
}

/** The three features after this one, wrapping round, for the cross-links at the foot of the page. */
function relatedFeatures(slug: string): FeatureData[] {
  const all = Object.values(features);
  const at = all.findIndex((f) => f.slug === slug);
  return [1, 2, 3].map((k) => all[(at + k) % all.length]).filter((f) => f.slug !== slug);
}

const COMMAND_PLAN_NAMES: Record<PlanName, string> = {
  Lite: 'Lite',
  Professional: 'Professional',
  Premium: 'Premium AI',
  Enterprise: 'Enterprise',
};

function planLabel(plan: PlanName, liteFrom?: FeatureData['liteFrom']): string {
  if (plan === 'Lite') return 'Included in every plan';
  const command = `Command from ${COMMAND_PLAN_NAMES[plan]}`;
  if (!liteFrom) return `Included in ${command}`;
  return liteFrom === 'Lite' ? `Included in every Lite plan · ${command}` : `Included from ${liteFrom} · ${command}`;
}

function Eyebrow({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color }}>
      {children}
    </p>
  );
}

export default function FeaturePage({ data }: { data: FeatureData }) {
  const { t } = useTranslation();
  const c = data.accentColor ?? '#1769ff';
  const cl = data.accentColorLight ?? '#eef5ff';
  const hero = data.heroImage;
  const ratio = hero.width / hero.height;
  /** Phone-shaped and square visuals sit in a narrower frame so they don't tower over the copy. */
  const heroFrame = ratio < 0.8 ? 'max-w-[340px]' : ratio < 1.2 ? 'max-w-[480px]' : '';
  const stepCount = data.howItWorks.length;
  const related = relatedFeatures(data.slug);
  const useCases = (data.useCases ?? [])
    .map((u) => ({ ...u, page: industries[u.industry] }))
    .filter((u) => u.page);
  const posts = (data.relatedPosts ?? []).map(getPostBySlug).filter((p) => p !== undefined);

  return (
    <div className="bg-white pt-24 text-gray-900">
      <SEO
        title={data.name}
        description={data.description}
        ogImage={hero.src.startsWith('http') ? hero.src : `https://geofleet.ai${hero.src}`}
        ogImageAlt={hero.alt}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://geofleet.ai' },
            { name: 'Features', url: 'https://geofleet.ai/features' },
            { name: data.name, url: `https://geofleet.ai/features/${data.slug}` },
          ]),
          faqJsonLd(data.faq),
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-white pb-16 pt-8 lg:pb-20 lg:pt-12">
        <div
          className="pointer-events-none absolute right-0 top-0 h-[420px] w-[min(560px,90vw)] translate-x-1/4 -translate-y-1/3 rounded-full opacity-[0.1] blur-3xl"
          style={{ backgroundColor: c }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-7xl px-6">
          <nav aria-label="Breadcrumb" className="mb-8 flex items-center gap-1.5 text-sm text-gray-500">
            <Link to="/features" className="hover:text-gray-900">
              Features
            </Link>
            <i className="ri-arrow-right-s-line rtl:rotate-180" aria-hidden />
            <span className="font-medium text-gray-900">{data.name}</span>
          </nav>
          <div className="grid items-center gap-12 lg:grid-cols-[5fr_6fr] lg:gap-16">
            <div>
              <div
                className="mb-6 inline-flex items-center gap-2 rounded-full border bg-white px-4 py-2 text-sm font-semibold shadow-sm"
                style={{ borderColor: cl, color: c }}
              >
                <i className={data.icon} aria-hidden />
                {data.tagline}
              </div>
              <h1 className="text-balance text-4xl font-bold tracking-tight text-gray-900 lg:text-5xl lg:leading-[1.08]">
                {data.headline}{' '}
                <span className="inline-block" style={{ color: c }}>
                  {data.headlineHighlight}
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600">{data.description}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <StartChooser
                  label={t('home.hero.ctaPrimary')}
                  className="inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-semibold text-white shadow-lg transition-opacity hover:opacity-90"
                  style={{ backgroundColor: c, boxShadow: `0 10px 24px -8px ${c}80` }}
                />
                <a
                  href={DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl border-2 border-gray-200 bg-white px-6 py-3.5 text-sm font-semibold text-gray-800 transition-colors hover:border-gray-400"
                >
                  <i className="ri-calendar-line" style={{ color: c }} aria-hidden />
                  {t('common.ctaBookDemo')}
                </a>
              </div>
              {data.plan && (
                <Link
                  to="/pricing"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
                >
                  <i className="ri-checkbox-circle-line" style={{ color: c }} aria-hidden />
                  {planLabel(data.plan, data.liteFrom)}
                  <span className="underline decoration-gray-300 underline-offset-4">See pricing</span>
                </Link>
              )}
              <dl className="mt-10 grid max-w-lg grid-cols-3 gap-6">
                {data.heroStats.map((s) => (
                  <div key={s.label} className="flex flex-col-reverse border-s-2 ps-4" style={{ borderColor: c }}>
                    <dt className="text-xs text-gray-500">{s.label}</dt>
                    <dd className="text-xl font-bold tracking-tight text-gray-900">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className={`mx-auto w-full ${heroFrame}`}>
              <div
                className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-1.5 shadow-2xl"
                style={{ boxShadow: `0 30px 60px -20px ${c}33` }}
              >
                <img
                  src={hero.src}
                  alt={hero.alt}
                  width={hero.width}
                  height={hero.height}
                  className="h-auto w-full rounded-xl"
                  fetchPriority="high"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before / after */}
      {data.beforeAfter && (
        <section className="border-t border-gray-100 bg-white py-16 lg:py-20" aria-labelledby="feature-before-after">
          <div className="mx-auto max-w-6xl px-6">
            <h2 id="feature-before-after" className="sr-only">
              Before and after {data.name}
            </h2>
            <div className="grid overflow-hidden rounded-3xl border border-gray-200 md:grid-cols-2">
              <div className="bg-slate-50 p-8 lg:p-10">
                <p className="mb-5 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-500">
                  <i className="ri-close-circle-line text-lg text-gray-400" aria-hidden />
                  Without {data.name}
                </p>
                <ul className="m-0 list-none space-y-4 p-0">
                  {data.beforeAfter.before.map((line) => (
                    <li key={line} className="flex gap-3 text-[15px] leading-relaxed text-gray-600">
                      <i className="ri-subtract-line mt-1 shrink-0 text-gray-400" aria-hidden />
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative bg-white p-8 lg:p-10">
                <div className="pointer-events-none absolute right-0 top-0 h-40 w-40 rounded-bl-full opacity-70" style={{ backgroundColor: cl }} aria-hidden />
                <p className="relative mb-5 flex items-center gap-2 text-sm font-bold uppercase tracking-wider" style={{ color: c }}>
                  <i className="ri-checkbox-circle-fill text-lg" aria-hidden />
                  With {data.name}
                </p>
                <ul className="relative m-0 list-none space-y-4 p-0">
                  {data.beforeAfter.after.map((line) => (
                    <li key={line} className="flex gap-3 text-[15px] font-medium leading-relaxed text-gray-800">
                      <i className="ri-check-line mt-1 shrink-0" style={{ color: c }} aria-hidden />
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Spotlights: image-led deep dives */}
      {data.spotlights && data.spotlights.length > 0 && (
        <section className="border-t border-gray-100 bg-white py-20 lg:py-24" aria-labelledby="feature-spotlights">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mx-auto mb-16 max-w-2xl text-center lg:mb-20">
              <Eyebrow color={c}>A closer look</Eyebrow>
              <h2 id="feature-spotlights" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
                {data.name}, in the product
              </h2>
            </div>
            <div className="space-y-20 lg:space-y-28">
              {data.spotlights.map((s, i) => {
                const flip = i % 2 === 1;
                const contain = s.fit === 'contain';
                return (
                  <article key={s.title} className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                    <div className={flip ? 'lg:order-2' : ''}>
                      <Eyebrow color={c}>{s.eyebrow}</Eyebrow>
                      <h3 className="mt-3 text-balance text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">{s.title}</h3>
                      <p className="mt-4 text-[17px] leading-relaxed text-gray-600">{s.body}</p>
                      <ul className="m-0 mt-6 list-none space-y-3 p-0">
                        {s.bullets.map((b) => (
                          <li key={b} className="flex gap-3 text-[15px] leading-relaxed text-gray-700">
                            <span
                              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px]"
                              style={{ backgroundColor: cl, color: c }}
                            >
                              <i className="ri-check-line" aria-hidden />
                            </span>
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className={flip ? 'lg:order-1' : ''}>
                      {contain ? (
                        <div
                          className="flex items-center justify-center rounded-3xl border border-gray-200 px-8 py-10"
                          style={{ background: `linear-gradient(160deg, ${cl} 0%, #ffffff 100%)` }}
                        >
                          <img
                            src={s.image.src}
                            alt={s.image.alt}
                            width={s.image.width}
                            height={s.image.height}
                            loading="lazy"
                            className="h-auto w-auto max-h-[520px] max-w-full rounded-2xl shadow-2xl"
                            style={{ boxShadow: `0 30px 60px -24px ${c}55` }}
                          />
                        </div>
                      ) : (
                        <div
                          className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-1.5"
                          style={{ boxShadow: `0 30px 60px -24px ${c}40` }}
                        >
                          <img
                            src={s.image.src}
                            alt={s.image.alt}
                            width={s.image.width}
                            height={s.image.height}
                            loading="lazy"
                            className="h-auto w-full rounded-xl"
                          />
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="border-t border-gray-100 bg-slate-50/70 py-20 lg:py-24" aria-labelledby="feature-steps">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <Eyebrow color={c}>{stepCount} steps</Eyebrow>
            <h2 id="feature-steps" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
              How it works
            </h2>
          </div>
          <ol
            className="relative m-0 grid list-none gap-10 p-0 md:grid-cols-[repeat(var(--steps),minmax(0,1fr))] md:gap-8"
            style={{ ['--steps' as string]: stepCount } as React.CSSProperties}
          >
            {/* Connector running through the step icons. */}
            <span
              className="absolute top-7 hidden h-px bg-gray-200 md:block"
              style={{ left: `${50 / stepCount}%`, right: `${50 / stepCount}%` }}
              aria-hidden
            />
            {data.howItWorks.map((step) => (
              <li key={step.title} className="relative text-center md:px-4">
                <div
                  className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-lg ring-8 ring-slate-50"
                  style={{ backgroundColor: c }}
                >
                  <i className={`${step.icon} text-2xl`} aria-hidden />
                </div>
                <p className="mt-5 text-xs font-bold uppercase tracking-[0.18em]" style={{ color: c }}>
                  Step {step.step}
                </p>
                <h3 className="mt-1.5 text-lg font-bold text-gray-900">{step.title}</h3>
                <p className="mx-auto mt-2 max-w-xs text-[15px] leading-relaxed text-gray-600">{step.description}</p>
              </li>
            ))}
          </ol>
          <p className="mt-12 text-center text-sm text-gray-500">
            See where {data.name} fits in the full flow on the{' '}
            <Link to="/how-it-works" className="font-semibold hover:underline" style={{ color: c }}>
              How it works
            </Link>{' '}
            page.
          </p>
        </div>
      </section>

      {/* App screens gallery (e.g. Driver App, Proof of Delivery) */}
      {data.heroScreens && data.heroScreens.length > 0 && (
        <section className="border-t border-gray-100 bg-white py-20 lg:py-24" aria-labelledby="feature-screens">
          <div className="mx-auto max-w-6xl px-6">
            <div className="mx-auto mb-12 max-w-2xl text-center">
              <Eyebrow color={c}>In the app</Eyebrow>
              <h2 id="feature-screens" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
                See it in action
              </h2>
            </div>
            <div className={`grid gap-6 lg:gap-8 ${data.heroScreens.length === 1 ? 'mx-auto max-w-xl' : 'md:grid-cols-3'}`}>
              {data.heroScreens.map((screen) => (
                <figure
                  key={screen.src}
                  className="m-0 flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow duration-300 hover:shadow-lg"
                >
                  <div className="flex flex-1 items-center justify-center p-6">
                    <img
                      src={screen.src}
                      alt={screen.title}
                      loading="lazy"
                      className="max-h-[min(65vh,440px)] w-auto max-w-full rounded-lg object-contain"
                    />
                  </div>
                  <figcaption className="border-t border-gray-100 px-5 py-4 text-center text-sm font-medium text-gray-700">
                    {screen.title}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Capabilities */}
      <section className="border-t border-gray-100 bg-white py-20 lg:py-24" aria-labelledby="feature-capabilities">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <Eyebrow color={c}>Capabilities</Eyebrow>
            <h2 id="feature-capabilities" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
              Everything in {data.name}
            </h2>
          </div>
          <ul className="m-0 grid list-none gap-x-10 gap-y-9 rounded-3xl border border-gray-200 bg-slate-50/60 p-8 sm:grid-cols-2 lg:grid-cols-3 lg:p-12">
            {data.capabilities.map((cap) => (
              <li key={cap.title} className="flex gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: cl, color: c }}>
                  <i className={`${cap.icon} text-xl`} aria-hidden />
                </div>
                <div className="min-w-0">
                  <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold text-gray-900">
                    {cap.title}
                    {cap.tag && (
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                        style={{ backgroundColor: cl, color: c }}
                      >
                        {cap.tag}
                      </span>
                    )}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600">{cap.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Use cases by industry */}
      {useCases.length > 0 && (
        <section className="border-t border-gray-100 bg-slate-50/70 py-20 lg:py-24" aria-labelledby="feature-use-cases">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
              <div>
                <Eyebrow color={c}>Who uses it</Eyebrow>
                <h2 id="feature-use-cases" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
                  Built for the way your fleet runs
                </h2>
              </div>
              <Link to="/industries" className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: c }}>
                All industries
                <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
              </Link>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {useCases.map((u) => (
                <Link
                  key={u.industry}
                  to={`/industries/${u.industry}`}
                  className="group flex flex-col rounded-2xl border border-gray-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{u.page.name}</p>
                  <h3 className="mt-2 flex items-center gap-1.5 text-lg font-bold text-gray-900">
                    {u.title}
                    <i className="ri-arrow-right-line text-gray-400 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" aria-hidden />
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{u.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="relative overflow-hidden bg-gray-950 py-20 lg:py-24" aria-labelledby="feature-benefits">
        <div
          className="pointer-events-none absolute -left-24 top-0 h-80 w-80 rounded-full opacity-20 blur-3xl"
          style={{ backgroundColor: c }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-6xl px-6">
          <h2 id="feature-benefits" className="max-w-xl text-3xl font-bold tracking-tight text-white lg:text-4xl">
            What changes with {data.name}
          </h2>
          <ul className="m-0 mt-12 grid list-none gap-10 p-0 sm:grid-cols-3 sm:gap-8">
            {data.benefits.map((b) => (
              <li key={b.label} className="border-t-2 pt-6" style={{ borderColor: c }}>
                <p className="text-4xl font-bold tracking-tight text-white lg:text-5xl">{b.value}</p>
                <h3 className="mt-3 text-base font-semibold text-white">{b.label}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">{b.description}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Integrations (optional) */}
      {data.integrations && data.integrations.length > 0 && (
        <section className="border-b border-gray-100 bg-slate-50/70 py-16" aria-labelledby="feature-integrations">
          <div className="mx-auto grid max-w-6xl items-center gap-8 px-6 lg:grid-cols-[2fr_3fr] lg:gap-14">
            <div>
              <h2 id="feature-integrations" className="text-2xl font-bold tracking-tight text-gray-900">
                Works with your stack
              </h2>
              <p className="mt-2 leading-relaxed text-gray-600">Connects with 50+ platforms out of the box.</p>
              <Link to="/integrations" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: c }}>
                See all integrations
                <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
              </Link>
            </div>
            <ul className="m-0 flex list-none flex-wrap gap-3 p-0">
              {data.integrations.map((name) => (
                <li key={name} className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm">
                  {name}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-white py-20 lg:py-24" aria-labelledby="feature-faq">
        <div className="mx-auto max-w-3xl px-6">
          <h2 id="feature-faq" className="mb-10 text-center text-3xl font-bold tracking-tight text-gray-900">
            {t('common.faqTitle')}
          </h2>
          <div className="space-y-3">
            {data.faq.map((item, i) => (
              <details
                key={item.question}
                open={i === 0}
                className="group rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm open:shadow-md"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-gray-900 [&::-webkit-details-marker]:hidden">
                  <h3 className="text-base">{item.question}</h3>
                  <i className="ri-add-line shrink-0 text-xl text-gray-400 transition-transform group-open:rotate-45" aria-hidden />
                </summary>
                <p className="mt-3 text-[15px] leading-relaxed text-gray-600">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related reading */}
      {posts.length > 0 && (
        <section className="border-t border-gray-100 bg-white py-16 lg:py-20" aria-labelledby="feature-reading">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
              <h2 id="feature-reading" className="text-2xl font-bold tracking-tight text-gray-900">
                Keep reading
              </h2>
              <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: c }}>
                All articles
                <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
              </Link>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {posts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group flex flex-col rounded-2xl border border-gray-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {p.category} · {p.readTime}
                  </p>
                  <h3 className="mt-2 text-base font-bold leading-snug text-gray-900 group-hover:underline">{p.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-gray-600">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* More features */}
      <section className="border-t border-gray-100 bg-slate-50/70 py-16 lg:py-20" aria-labelledby="feature-more">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <h2 id="feature-more" className="text-2xl font-bold tracking-tight text-gray-900">
              Explore more features
            </h2>
            <Link to="/features" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-600">
              All features
              <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
            </Link>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {related.map((f) => (
              <Link
                key={f.slug}
                to={`/features/${f.slug}`}
                className="group flex gap-4 rounded-2xl border border-gray-200 bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                  style={{ backgroundColor: f.accentColorLight ?? '#eef5ff', color: f.accentColor ?? '#1769ff' }}
                >
                  <i className={`${f.icon} text-xl`} aria-hidden />
                </div>
                <div className="min-w-0">
                  <h3 className="flex items-center gap-1.5 text-base font-semibold text-gray-900">
                    {f.name}
                    <i className="ri-arrow-right-line text-gray-400 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" aria-hidden />
                  </h3>
                  <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-gray-600">{f.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-gray-800 bg-gray-950 py-16 lg:py-20">
        <div
          className="pointer-events-none absolute -right-20 top-0 h-96 w-96 rounded-full opacity-20 blur-3xl"
          style={{ backgroundColor: c }}
          aria-hidden
        />
        <div className="relative z-10 mx-auto max-w-2xl px-6 text-center">
          <h2 className="text-2xl font-bold text-white lg:text-3xl">Ready to try {data.name}?</h2>
          <p className="mt-4 leading-relaxed text-gray-300">Try Geofleet Lite free for 7 days, or set up Geofleet Command for a larger fleet.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <StartChooser
              label={t('home.hero.ctaPrimary')}
              className="inline-flex min-w-[200px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/35 transition-all hover:bg-blue-500"
            />
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-w-[200px] items-center justify-center gap-2 rounded-xl border-2 border-white/30 bg-transparent px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-white/10"
            >
              <i className="ri-calendar-line" aria-hidden />
              {t('common.ctaBookDemo')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
